import React, { useEffect, useRef, useState } from "react";

/**
 * EliteKanbanBoardV2.jsx — React 17.0.2 (no external libs)
 * V2.4 highlights:
 * - Trello-like POPUP Job Card (tabs + production chat + cancel/reinstate)
 * - Day-only UI, backend persists ISO datetimes (YYYY-MM-DDT00:00:00)
 * - Overdue nudges, soft confirmations (never hard-block)
 * - Rank-first ordering with drag-to-reorder + ↑/↓ (rank PATCH)
 * - Infinite scroll (Planning, 15/page), adjacency-only lane moves, Paused rules
 */

const STATUSES = [
  { key: "Planned", label: "Upcoming / Planning" },
  { key: "Scheduled", label: "Scheduled" },
  { key: "In_Production", label: "In Production" },
  { key: "QC", label: "QC" },
  { key: "Completed", label: "Completed" },
  { key: "Paused", label: "Paused / Hold" },
];
const STATUS_INDEX = STATUSES.reduce((m, s, i) => ((m[s.key] = i), m), {});

const DATE_FMT = (d) => (d ? new Date(d).toLocaleDateString("en-CA") : "–"); // Canadian yyyy-mm-dd
const todayYMD = () => new Date().toISOString().slice(0, 10);
const toISOStart = (ymd) =>
  ymd && ymd.length === 10 ? `${ymd}T00:00:00` : ymd || "";
const toISOEnd = (ymd) =>
  ymd && ymd.length === 10 ? `${ymd}T23:59:59` : ymd || "";
function daysLate(ymd) {
  if (!ymd) return 0;
  const a = new Date(`${ymd}T00:00:00`);
  const b = new Date(`${todayYMD()}T00:00:00`);
  return Math.max(0, Math.floor((b - a) / 86400000));
}

// Department inference
const DEPT_MATCHERS = {
  Screening: [/screen ?print/i, /pad ?print/i, /silk/i],
  Embroidery: [/embroider/i],
  "Direct to Film": [/\bDTF\b/i, /direct\s*to\s*film/i],
  Engraving: [/engrave/i, /laser/i],
  Cresting: [/crest/i, /heat ?press/i, /vinyl/i],
  Awards: [/award/i, /trophy/i],
};
const CHARGE_ID_TO_DEPT = {
  15: "Embroidery",
  45: "Screening",
  107: "Direct to Film",
};
function inferDepartmentForDeco(deco) {
  if (!deco) return "";
  if (deco.chargeId != null && CHARGE_ID_TO_DEPT[deco.chargeId])
    return CHARGE_ID_TO_DEPT[deco.chargeId];
  const name = String(deco.chargeName || "");
  for (const [dept, tests] of Object.entries(DEPT_MATCHERS))
    if (tests.some((re) => re.test(name))) return dept;
  return "";
}

// ────────────────────────────────────────────────────────────────────────────
// Job Card POPUP (Trello-like)
// Tabs: Overview (client-requested sections), Processes, BOM, Chat, Activity
// Includes Cancel/Reinstate controls with lifecycle logging.
// ────────────────────────────────────────────────────────────────────────────
function JobCardModal({
  open,
  onClose,
  card,
  onPatchCard,
  onCancelCard,
  onReinstateCard,
}) {
  const [tab, setTab] = useState("overview");
  const [processes, setProcesses] = useState(card?.processes || []);
  const [bom, setBOM] = useState(card?.bom || []);
  const [chat, setChat] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatText, setChatText] = useState("");

  useEffect(() => {
    if (!open || !card) return;
    setTab("overview");
    setProcesses(card.processes || []);
    setBOM(card.bom || []);
    // Load chat if this is a real step (server owns messages)
    (async () => {
      if (!card.__isStep) {
        setChat([]);
        return;
      }
      setChatLoading(true);
      try {
        const r = await fetch(
          `http://localhost:3001/production/steps/${encodeURIComponent(card.id)}/messages`
        );
        if (r.ok) {
          const js = await r.json();
          setChat(Array.isArray(js?.data) ? js.data : []);
        } else setChat([]);
      } catch {
        setChat([]);
      } finally {
        setChatLoading(false);
      }
    })();
  }, [open, card]);

  if (!open || !card) return null;

  const o = card.raw?.order || {};
  const it = card.raw?.item || {};
  const d = card.raw?.deco || {};
  const ctx = {
    customer: card.customer || o.customerName,
    qty: it.quantity ?? card.qtyPlanned ?? 0,
    inHandsDate: o.inHandsDate || card.dueDate,
  };

  const addProcess = () =>
    setProcesses((p) => [
      ...p,
      {
        id: `proc-${Date.now()}`,
        name: "",
        owner: "",
        type: "Manual",
        machine: "",
        estMinutes: "",
        dueDate: "",
      },
    ]);
  const addBOM = () =>
    setBOM((b) => [
      ...b,
      {
        id: `bom-${Date.now()}`,
        part: "",
        desc: "",
        qty: "",
        unit: "",
        supplier: "",
      },
    ]);

  const saveMeta = async () => {
    // Persist processes/BOM (optional, stub)
    try {
      if (card.__isStep) {
        await fetch(
          `http://localhost:3001/production/steps/${encodeURIComponent(card.id)}/processes`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(processes),
          }
        );
        await fetch(`http://localhost:3001/production/steps/${encodeURIComponent(card.id)}/bom`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bom),
        });
      }
      onPatchCard(card.id, { processes, bom });
      onClose();
    } catch {
      // Soft fail; still patch UI
      onPatchCard(card.id, { processes, bom });
      onClose();
    }
  };

  const sendChat = async () => {
    const text = chatText.trim();
    if (!text) return;
    setChatText("");
    const localMsg = {
      id: `local-${Date.now()}`,
      text,
      when: new Date().toISOString(),
      author: "You",
    };
    setChat((c) => [...c, localMsg]);
    try {
      if (card.__isStep) {
        await fetch(
          `http://localhost:3001/production/steps/${encodeURIComponent(card.id)}/messages`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
          }
        );
      }
    } catch {
      // ignore; message stays locally
    }
  };

  const cancelFlow = () => {
    const reason = window.prompt("Cancel this order/step? Add a reason:");
    if (reason == null) return;
    onCancelCard(card, reason);
  };
  const reinstateFlow = () => {
    const reason =
      window.prompt("Reinstate this order/step? Add a note (optional):") || "";
    onReinstateCard(card, reason);
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.card} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h3 style={{ margin: 0 }}>
              {card.orderNumber || card.title || "Job Card"}
            </h3>
            {card.cancelled && <span style={ui.cancelBadge}>CANCELLED</span>}
            {card.status && (
              <span style={ui.statusPill}>
                {card.status.replace(/_/g, " ")}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {!card.cancelled ? (
              <button style={ui.btnDanger} onClick={cancelFlow}>
                Cancel order
              </button>
            ) : (
              <button style={ui.btnWarn} onClick={reinstateFlow}>
                Reinstate
              </button>
            )}
            <button style={ui.btnGhost} onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={tabs.wrap}>
          {["overview", "processes", "bom", "chat", "activity"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{ ...tabs.tab, ...(tab === t ? tabs.active : null) }}
            >
              {t === "overview" ? "Details" : t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview: Client-requested sections */}
        {tab === "overview" && (
          <div
            style={{
              padding: 12,
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 12,
            }}
          >
            <Section title="Project Information">
              <Row l="Project name" v={o.projectName} />
              <Row l="Project Number" v={o.orderNumber || card.orderNumber} />
              <RowChip
                l="PO Numbers"
                list={Array.isArray(o.poNumbers) ? o.poNumbers : []}
              />
            </Section>

            <Section title="Product information">
              <Row l="SKU" v={it.productId || card.sku} />
              <Row l="Sizes" v={(it.sizes || []).join(", ")} />
              <Row l="Colors" v={(it.colors || []).join(", ")} />
              <Row l="Net cost goods" v={money(it.netCostGoods)} />
              <Row l="Retail cost goods" v={money(it.retailCostGoods)} />
              <Row l="Supplier information" v={it.supplierName} />
              <Row l="Category" v={it.category} />
            </Section>

            <Section title="Decoration information">
              <Row
                l="Deco department"
                v={
                  card.claimedDept ||
                  card.department ||
                  inferDepartmentForDeco(d)
                }
              />
              <Row l="Matrix pricing" v={d.chargeName || card.matrix} />
              <Row l="Net cost goods" v={money(d.netCostGoods)} />
              <Row l="Retail cost goods" v={money(d.retailCostGoods)} />
              <Row l="Location" v={d.location || card.location} />
            </Section>

            <Section title="Art information">
              {d.artProofUrl ? (
                <a
                  href={d.artProofUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "underline" }}
                >
                  Open proof
                </a>
              ) : (
                <div style={ui.value}>–</div>
              )}
            </Section>

            <div style={{ gridColumn: "1/-1" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gap: 12,
                }}
              >
                <Row l="Customer" v={ctx.customer} />
                <Row l="Qty" v={ctx.qty} />
                <Row l="In-hands" v={DATE_FMT(ctx.inHandsDate)} />
                <Row
                  l="Scheduled Date"
                  v={card.plannedStart ? DATE_FMT(card.plannedStart) : "–"}
                />
              </div>
            </div>
          </div>
        )}

        {/* Processes */}
        {tab === "processes" && (
          <div style={{ padding: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong>Processes</strong>
              <button style={ui.linkBtn} onClick={addProcess}>
                + Add process
              </button>
            </div>
            <div style={{ marginTop: 8, display: "grid", gap: 10 }}>
              {(processes || []).map((p, idx) => (
                <div key={p.id} style={ui.rowCard}>
                  <Input l="#" value={String(idx + 1)} readOnly />
                  <Input
                    l="Name"
                    value={p.name}
                    onChange={(v) =>
                      updateAt(setProcesses, idx, { ...p, name: v })
                    }
                  />
                  <Input
                    l="Person"
                    value={p.owner}
                    onChange={(v) =>
                      updateAt(setProcesses, idx, { ...p, owner: v })
                    }
                  />
                  <Select
                    l="Type"
                    value={p.type || "Manual"}
                    onChange={(v) =>
                      updateAt(setProcesses, idx, { ...p, type: v })
                    }
                    options={["Manual", "Machine"]}
                  />
                  <Input
                    l="Machine (opt)"
                    value={p.machine || ""}
                    onChange={(v) =>
                      updateAt(setProcesses, idx, { ...p, machine: v })
                    }
                  />
                  <Input
                    l="Est. minutes"
                    type="number"
                    value={p.estMinutes || ""}
                    onChange={(v) =>
                      updateAt(setProcesses, idx, { ...p, estMinutes: v })
                    }
                  />
                  <Input
                    l="Due date"
                    type="date"
                    value={p.dueDate || ""}
                    onChange={(v) =>
                      updateAt(setProcesses, idx, { ...p, dueDate: v })
                    }
                  />
                  <button
                    className="danger"
                    style={ui.btnDanger}
                    onClick={() => removeAt(setProcesses, idx)}
                  >
                    Delete
                  </button>
                </div>
              ))}
              {!processes?.length && (
                <div style={ui.empty}>No processes yet.</div>
              )}
            </div>
          </div>
        )}

        {/* BOM */}
        {tab === "bom" && (
          <div style={{ padding: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong>BOM</strong>
              <button style={ui.linkBtn} onClick={addBOM}>
                + Add line
              </button>
            </div>
            <div style={{ marginTop: 8, display: "grid", gap: 10 }}>
              {(bom || []).map((b, idx) => (
                <div key={b.id} style={ui.rowCard}>
                  <Input
                    l="Part #"
                    value={b.part}
                    onChange={(v) => updateAt(setBOM, idx, { ...b, part: v })}
                  />
                  <Input
                    l="Description"
                    value={b.desc}
                    onChange={(v) => updateAt(setBOM, idx, { ...b, desc: v })}
                  />
                  <Input
                    l="Qty"
                    type="number"
                    value={b.qty}
                    onChange={(v) => updateAt(setBOM, idx, { ...b, qty: v })}
                  />
                  <Input
                    l="Unit"
                    value={b.unit}
                    onChange={(v) => updateAt(setBOM, idx, { ...b, unit: v })}
                  />
                  <Input
                    l="Supplier"
                    value={b.supplier}
                    onChange={(v) =>
                      updateAt(setBOM, idx, { ...b, supplier: v })
                    }
                  />
                  <button
                    className="danger"
                    style={ui.btnDanger}
                    onClick={() => removeAt(setBOM, idx)}
                  >
                    Delete
                  </button>
                </div>
              ))}
              {!bom?.length && <div style={ui.empty}>No BOM lines yet.</div>}
            </div>
          </div>
        )}

        {/* Chat */}
        {tab === "chat" && (
          <div style={{ padding: 12, display: "grid", gap: 8 }}>
            <div style={ui.subtle}>Order / Production Chat</div>
            <div style={chatStyles.box}>
              {chatLoading ? (
                <div style={ui.subtle}>Loading…</div>
              ) : (chat || []).length ? (
                (chat || []).map((m) => (
                  <div key={m.id} style={chatStyles.msg}>
                    <div style={chatStyles.meta}>
                      <b>{m.author || "User"}</b> •{" "}
                      {new Date(m.when || Date.now()).toLocaleString()}
                    </div>
                    <div>{m.text}</div>
                  </div>
                ))
              ) : (
                <div style={ui.empty}>No messages yet.</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                placeholder="Type a message…"
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                style={{ flex: 1 }}
              />
              <button style={ui.btnPrimary} onClick={sendChat}>
                Send
              </button>
            </div>
          </div>
        )}

        {/* Activity */}
        {tab === "activity" && (
          <div style={{ padding: 12 }}>
            <div style={ui.subtle}>Lifecycle</div>
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              {(card.lifecycle || []).length ? (
                card.lifecycle.map((ev, i) => (
                  <li key={i}>
                    <b>{ev.when || ""}</b>: {ev.text || ""}
                  </li>
                ))
              ) : (
                <li>–</li>
              )}
            </ol>
          </div>
        )}

        <footer style={{ display: "flex", gap: 8, padding: 12 }}>
          <button style={ui.btnPrimary} onClick={saveMeta}>
            Save
          </button>
          <button style={ui.btnGhost} onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={ui.section}>
      <div style={{ ...ui.subtle, marginBottom: 6, fontWeight: 600 }}>
        {title}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 10,
        }}
      >
        {children}
      </div>
    </div>
  );
}
function Row({ l, v }) {
  return (
    <div>
      <div style={ui.label}>{l}</div>
      <div style={ui.value}>{v || v === 0 ? v : "–"}</div>
    </div>
  );
}
function RowChip({ l, list }) {
  return (
    <div>
      <div style={ui.label}>{l}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {(list || []).length ? (
          list.map((x, i) => (
            <span key={i} style={ui.chip}>
              {x}
            </span>
          ))
        ) : (
          <span style={ui.value}>–</span>
        )}
      </div>
    </div>
  );
}
const money = (n) => (typeof n === "number" ? n.toFixed(2) : "–");

function Input({ l, value, onChange, type = "text", readOnly }) {
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={ui.subtle}>{l}</span>
      <input
        type={type}
        value={value ?? ""}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </label>
  );
}
function Select({ l, value, onChange, options = [] }) {
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={ui.subtle}>{l}</span>
      <select value={value || ""} onChange={(e) => onChange?.(e.target.value)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
const updateAt = (set, i, v) =>
  set((list) => list.map((x, idx) => (idx === i ? v : x)));
const removeAt = (set, i) => set((list) => list.filter((_, idx) => idx !== i));

// ────────────────────────────────────────────────────────────────────────────
// Move Gate (UI date-only; backend ISO datetime)
// ────────────────────────────────────────────────────────────────────────────
function MoveGateModal({ to, fields, onCancel, onConfirm }) {
  const [form, setForm] = useState(
    fields || {
      operator: "",
      machine: "",
      plannedStart: "",
      plannedFinish: "",
      reason: "",
    }
  );
  const req = to === "Scheduled";
  const disabled =
    req &&
    (!form.operator ||
      !form.machine ||
      !form.plannedStart ||
      !form.plannedFinish);
  return (
    <div style={modal.overlay}>
      <div style={{ ...modal.card, width: "min(720px,95vw)" }}>
        <div style={modal.header}>
          <h3 style={{ margin: 0 }}>Move to {to}</h3>
          <button onClick={onCancel} style={ui.btnGhost}>
            Cancel
          </button>
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
        >
          <label>
            <div>Operator *</div>
            <input
              value={form.operator}
              onChange={(e) => setForm({ ...form, operator: e.target.value })}
            />
          </label>
          <label>
            <div>Machine *</div>
            <input
              value={form.machine}
              onChange={(e) => setForm({ ...form, machine: e.target.value })}
            />
          </label>
          <label>
            <div>Planned Start *</div>
            <input
              type="date"
              value={form.plannedStart}
              onChange={(e) =>
                setForm({ ...form, plannedStart: e.target.value })
              }
            />
          </label>
          <label>
            <div>Planned Finish *</div>
            <input
              type="date"
              value={form.plannedFinish}
              onChange={(e) =>
                setForm({ ...form, plannedFinish: e.target.value })
              }
            />
          </label>
          <label style={{ gridColumn: "1/-1" }}>
            <div>Reason (optional)</div>
            <input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </label>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            disabled={disabled}
            onClick={() => onConfirm(form)}
            style={{ ...ui.btnPrimary, opacity: disabled ? 0.6 : 1 }}
          >
            Confirm Move
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main Board
// ────────────────────────────────────────────────────────────────────────────
export default function EliteKanbanBoardV2() {
  const [columns, setColumns] = useState(() => emptyColumns());
  const [error, setError] = useState("");
  const [planningPage, setPlanningPage] = useState(1);
  const [planningHasMore, setPlanningHasMore] = useState(true);
  const [planningLoading, setPlanningLoading] = useState(false);

  // Popup state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCard, setModalCard] = useState(null);

  // Move Gate
  const [moveModal, setMoveModal] = useState({
    open: false,
    to: null,
    card: null,
    fields: {
      operator: "",
      machine: "",
      plannedStart: "",
      plannedFinish: "",
      reason: "",
    },
  });

  // Scroll refs & drag state
  const listRefs = useRef(
    Object.fromEntries(STATUSES.map((s) => [s.key, React.createRef()]))
  ).current;
  const dragRef = useRef({
    cardId: null,
    from: null,
    fromIndex: -1,
    overIndex: -1,
    overCol: null,
    lastMain: null,
  });

  useEffect(() => {
    loadPlanning(1, true); /* eslint-disable-next-line */
  }, []);

  async function loadPlanning(page, replace = false) {
    if (planningLoading || (!replace && !planningHasMore)) return;
    setPlanningLoading(true);
    setError("");
    try {
      const r = await fetch(`http://localhost:3001/production/orders?page=${page}&pageSize=15`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const js = await r.json();
      const cards = buildPlanningCards(js?.data || []);
      setColumns((prev) => {
        const next = replace ? emptyColumns() : clone(prev);
        const seen = new Set((next.Planned || []).map((c) => c.id));
        for (const c of cards)
          if (!seen.has(c.id))
            next.Planned.unshift(assignRankTop(next.Planned, c));
        return next;
      });
      const total = Number(js?.total ?? 0);
      const pageSize = Number(js?.pageSize ?? 15);
      const cur = Number(js?.page ?? page);
      setPlanningHasMore(cur * pageSize < total);
      setPlanningPage(cur);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setPlanningLoading(false);
    }
  }

  // Overdue “bubble up” once (soft nudge)
  useEffect(() => {
    setColumns((prev) => {
      const next = clone(prev);
      const list = next.Scheduled || [];
      let changed = false;
      const maxRank = list.reduce((m, c) => Math.max(m, c.rank || 0), 0);
      for (const c of list) {
        if (
          !c.startedAt &&
          daysLate(c.plannedStart) > 0 &&
          (c.rank || 0) <= maxRank
        ) {
          c.rank = maxRank + 50;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  // Helpers
  function emptyColumns() {
    const o = {};
    STATUSES.forEach((s) => (o[s.key] = []));
    return o;
  }
  const clone = (x) => JSON.parse(JSON.stringify(x));
  function buildPlanningCards(orders) {
    const out = [];
    for (const o of orders) {
      for (const it of o.items || []) {
        const decos = (it.decorations || []).filter(Boolean);
        if (!decos.length) continue;
        for (const d0 of decos) {
          const dept = inferDepartmentForDeco(d0);
          out.push({
            id: `ORD-${o.orderNumber}-${it.itemId}-${d0.decorationId}`,
            status: "Planned",
            orderNumber: o.orderNumber,
            customer: o.customerName || "",
            productName: it.productName || "",
            sku: it.productId || "",
            matrix: d0.chargeName || "",
            location: d0.location || "",
            qtyPlanned: Number(it.quantity || 0) || 0,
            qtyDone: 0,
            dueDate: o.inHandsDate || "",
            department: dept,
            claimedDept: "",
            lifecycle: [],
            rank: 0,
            __isStep: false, // becomes true after scheduling
            raw: {
              order: {
                orderNumber: o.orderNumber,
                customerName: o.customerName,
                inHandsDate: o.inHandsDate,
                projectName: o.projectName,
                poNumbers: o.poNumbers || [],
              },
              item: {
                itemId: it.itemId,
                productId: it.productId,
                productName: it.productName,
                quantity: it.quantity,
                sizes: it.sizes || [],
                colors: it.colors || [],
                netCostGoods: it.netCostGoods,
                retailCostGoods: it.retailCostGoods,
                supplierName: it.supplierName,
                category: it.category,
              },
              deco: {
                decorationId: d0.decorationId,
                chargeId: d0.chargeId,
                chargeName: d0.chargeName,
                location: d0.location,
                netCostGoods: d0.netCostGoods,
                retailCostGoods: d0.retailCostGoods,
                artProofUrl: d0.artProofUrl,
              },
            },
          });
        }
      }
    }
    return out;
  }
  function assignRankTop(arr, card) {
    const maxRank = arr.reduce((m, c) => Math.max(m, c.rank || 0), 0);
    return { ...card, rank: maxRank + 1000 };
  }
  function computeRankForIndex(arr, index) {
    const above = arr[index - 1];
    const below = arr[index + 1];
    if (!above && !below) return 1000;
    if (!above) return (below?.rank || 0) + 1000;
    if (!below) return (above?.rank || 0) - 1000;
    return Math.round(((above.rank || 0) + (below.rank || 0)) / 2);
  }

  // Persistence
  function patchStatus(id, body) {
    return fetch(`http://localhost:3001/production/steps/${encodeURIComponent(id)}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => {
      if (!r.ok) throw new Error(`Status save failed (${r.status})`);
    });
  }
  function patchRank(id, rank) {
    return fetch(`http://localhost:3001/production/steps/${encodeURIComponent(id)}/rank`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rank }),
    }).catch(() => {});
  }

  // DND
  function onDragStart(e, card, fromCol, fromIndex) {
    const isMain = fromCol !== "Paused";
    dragRef.current = {
      cardId: card.id,
      from: fromCol,
      fromIndex,
      overIndex: fromIndex,
      overCol: fromCol,
      lastMain: isMain ? fromCol : card._lastMain || "Planned",
    };
    e.dataTransfer.effectAllowed = "move";
  }
  function onDragEnterCard(e, colKey, overIndex) {
    if (!dragRef.current.cardId) return;
    dragRef.current.overIndex = overIndex;
    dragRef.current.overCol = colKey;
  }
  function onListOver(e, toCol) {
    e.preventDefault();
    const el = listRefs[toCol]?.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const topZone = rect.top + 40,
      botZone = rect.bottom - 40;
    if (e.clientY < topZone) el.scrollTop -= 10;
    else if (e.clientY > botZone) el.scrollTop += 10;
  }
  function onDrop(e, toCol) {
    e.preventDefault();
    const { cardId, from, overIndex, overCol, lastMain } =
      dragRef.current || {};
    if (!cardId || !from) return;

    // Same-lane reorder
    if (
      from === toCol &&
      overCol === toCol &&
      typeof overIndex === "number" &&
      overIndex >= 0
    ) {
      setColumns((prev) => {
        const next = clone(prev);
        const arr = next[toCol];
        const i = arr.findIndex((c) => c.id === cardId);
        if (i === -1) return prev;
        const [moved] = arr.splice(i, 1);
        const j = overIndex >= arr.length ? arr.length : overIndex;
        arr.splice(j, 0, moved);
        const newRank = computeRankForIndex(arr, j);
        moved.rank = newRank;
        if (toCol !== "Planned" && moved.__isStep) patchRank(moved.id, newRank);
        return next;
      });
      return;
    }

    // Cross-lane constraints
    if (toCol !== "Paused" && from !== "Paused") {
      const fi = STATUS_INDEX[from],
        ti = STATUS_INDEX[toCol];
      if (Math.abs(ti - fi) !== 1) {
        alert("Only adjacent moves allowed (except Paused/Hold).");
        return;
      }
    }

    setColumns((prev) => {
      const next = clone(prev);
      const fromArr = next[from] || [];
      const i = fromArr.findIndex((c) => c.id === cardId);
      if (i === -1) return prev;
      const [moved] = fromArr.splice(i, 1);

      // Move gate to Scheduled
      if (toCol === "Scheduled") {
        fromArr.splice(i, 0, moved);
        setMoveModal({
          open: true,
          to: "Scheduled",
          card: moved,
          fields: {
            operator: "",
            machine: "",
            plannedStart: "",
            plannedFinish: "",
            reason: "",
          },
        });
        return next;
      }

      // Paused rules
      if (toCol === "Paused") {
        moved._lastMain = from === "Paused" ? lastMain : from;
        moved.lifecycle = moved.lifecycle || [];
        moved.lifecycle.push({
          when: new Date().toLocaleString(),
          text: `Moved to Paused/Hold from ${from}`,
        });
      }
      if (from === "Paused" && toCol !== (moved._lastMain || "Planned")) {
        alert(`Paused card can only return to ${moved._lastMain}.`);
        fromArr.splice(i, 0, moved);
        return next;
      }

      // Soft confirm: late start
      if (
        toCol === "In_Production" &&
        moved.plannedStart &&
        daysLate(moved.plannedStart) > 0
      ) {
        const ok = window.confirm(
          `Scheduled for ${moved.plannedStart}. Start now anyway? (Cancel = reschedule to today)`
        );
        if (!ok) {
          // reschedule instead; keep in Scheduled
          moved.plannedStart = todayYMD();
          moved.lifecycle = [
            ...(moved.lifecycle || []),
            { when: new Date().toLocaleString(), text: "Rescheduled to today" },
          ];
          fromArr.splice(i, 0, moved);
          if (moved.__isStep) {
            patchStatus(moved.id, {
              fromStatus: "Scheduled",
              toStatus: "Scheduled",
              plannedStart: toISOStart(moved.plannedStart),
              plannedFinish: toISOEnd(moved.plannedStart),
              reason: "Rescheduled on start",
            });
          }
          return next;
        }
      }

      // Clear claim if back to Planning
      if (toCol === "Planned") {
        moved.claimedDept = "";
        moved.lifecycle = moved.lifecycle || [];
        moved.lifecycle.push({
          when: new Date().toLocaleString(),
          text: "Returned to Planning (open to all)",
        });
      }

      moved.status = toCol;
      next[toCol].unshift(assignRankTop(next[toCol], moved));

      // Persist status (non-blocking) if it's a step
      if (moved.__isStep && toCol !== "Planned") {
        patchStatus(moved.id, { fromStatus: from, toStatus: toCol }).catch(
          () => {}
        );
      }
      return next;
    });
  }

  // Planning infinite scroll
  function onScrollPlanning(e) {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom && planningHasMore && !planningLoading)
      loadPlanning(planningPage + 1);
  }

  // Quick helpers for modal
  function onPatchCard(id, patch) {
    setColumns((prev) => {
      const next = clone(prev);
      const all = Object.keys(next).flatMap((k) => next[k]);
      const c = all.find((x) => x.id === id);
      if (c) Object.assign(c, patch);
      if (modalCard?.id === id) setModalCard({ ...modalCard, ...patch });
      return next;
    });
  }
  function onCancelCard(card, reason) {
    const snapshot = JSON.stringify(columns);
    setColumns((prev) => {
      const next = clone(prev);
      const allK = Object.keys(next);
      for (const k of allK) {
        const arr = next[k];
        const i = arr.findIndex((c) => c.id === card.id);
        if (i !== -1) {
          arr[i].cancelled = true;
          arr[i].lifecycle = [
            ...(arr[i].lifecycle || []),
            {
              when: new Date().toLocaleString(),
              text: `Cancelled: ${reason || "—"}`,
            },
          ];
          break;
        }
      }
      return next;
    });
    if (card.__isStep) {
      patchStatus(card.id, {
        fromStatus: card.status,
        toStatus: "Cancelled",
        reason,
      }).catch(() => setColumns(JSON.parse(snapshot)));
    }
  }
  function onReinstateCard(card, note) {
    const snapshot = JSON.stringify(columns);
    setColumns((prev) => {
      const next = clone(prev);
      const allK = Object.keys(next);
      for (const k of allK) {
        const arr = next[k];
        const i = arr.findIndex((c) => c.id === card.id);
        if (i !== -1) {
          arr[i].cancelled = false;
          arr[i].lifecycle = [
            ...(arr[i].lifecycle || []),
            {
              when: new Date().toLocaleString(),
              text: `Reinstated: ${note || "—"}`,
            },
          ];
          break;
        }
      }
      return next;
    });
    if (card.__isStep) {
      // Return to Scheduled (common case); adjust if you want to return to previous main column
      patchStatus(card.id, {
        fromStatus: "Cancelled",
        toStatus: "Scheduled",
        reason: note,
      }).catch(() => setColumns(JSON.parse(snapshot)));
    }
  }

  // Move Gate confirm → make it a step, persist ISO datetimes
  async function confirmSchedule(fields) {
    const yStart = fields.plannedStart;
    const yFinish = fields.plannedFinish;
    const isoStart = toISOStart(yStart);
    const isoFinish = toISOEnd(yFinish);

    const snapshot = JSON.stringify(columns);
    const next = JSON.parse(snapshot);

    // find and move
    const fromKey = Object.keys(next).find((k) =>
      (next[k] || []).some((c) => c.id === moveModal.card.id)
    );
    if (!fromKey)
      return setMoveModal({ open: false, to: null, card: null, fields: {} });
    const arr = next[fromKey];
    const idx = arr.findIndex((c) => c.id === moveModal.card.id);
    if (idx === -1)
      return setMoveModal({ open: false, to: null, card: null, fields: {} });
    const orig = arr[idx];
    arr.splice(idx, 1);

    const scheduled = {
      ...orig,
      status: "Scheduled",
      claimedDept:
        orig.department ||
        inferDepartmentForDeco(orig.raw?.deco) ||
        "Department",
      plannedStart: yStart, // UI date-only
      plannedFinish: yFinish, // UI date-only
      operator: fields.operator,
      machine: fields.machine,
      lifecycle: [
        ...(orig.lifecycle || []),
        {
          when: new Date().toLocaleString(),
          text: `Scheduled: ${fields.operator || "Unassigned"} on ${
            fields.machine || "TBD"
          }`,
        },
      ],
      __isStep: true,
    };
    next.Scheduled.unshift(assignRankTop(next.Scheduled, scheduled));
    setColumns(next);
    setMoveModal({ open: false, to: null, card: null, fields: {} });

    try {
      await patchStatus(scheduled.id, {
        fromStatus: fromKey,
        toStatus: "Scheduled",
        operator: fields.operator,
        machine: fields.machine,
        plannedStart: isoStart,
        plannedFinish: isoFinish,
        qty: scheduled.qtyPlanned,
        reason: fields.reason || "",
      });
      patchRank(scheduled.id, scheduled.rank);
    } catch (e) {
      alert(e.message || "Could not save schedule");
      setColumns(JSON.parse(snapshot)); // rollback
    }
  }

  // UI events
  function openCard(card) {
    setModalCard(card);
    setModalOpen(true);
  }
  function onScrollCol(key, e) {
    if (key === "Planned") onScrollPlanning(e);
  }

  // Render
  return (
    <div style={ui.wrap}>
      <header style={ui.toolbar}>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <strong>Production Board</strong>
          {planningLoading && <span style={ui.pill}>Loading…</span>}
          {error && <span style={ui.errPill}>Error: {error}</span>}
        </div>
      </header>
      <div style={ui.board}>
        {STATUSES.map(({ key, label }) => (
          <section
            key={key}
            style={ui.column}
            onDragOver={(e) => onListOver(e, key)}
            onDrop={(e) => onDrop(e, key)}
          >
            <div style={ui.colHeader}>
              <h3 style={{ margin: 0 }}>{label}</h3>
            </div>
            <div
              ref={listRefs[key]}
              style={ui.cardList}
              onScroll={(e) => onScrollCol(key, e)}
            >
              {(columns[key] || []).map((c, idx) => (
                <article
                  key={c.id}
                  data-index={idx}
                  style={{
                    ...ui.card,
                    ...(c.cancelled ? ui.cardCancelled : null),
                  }}
                  draggable={!c.cancelled}
                  onDragStart={(e) => onDragStart(e, c, key, idx)}
                  onDragEnter={(e) => onDragEnterCard(e, key, idx)}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 8,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700 }}>
                        {c.orderNumber || c.title || "—"}{" "}
                        {key === "Scheduled" &&
                          !c.startedAt &&
                          c.plannedStart &&
                          daysLate(c.plannedStart) > 0 && (
                            <span style={ui.overduePill}>
                              Overdue {daysLate(c.plannedStart)}d
                            </span>
                          )}
                      </div>
                      <div style={{ fontSize: 12, color: "#555" }}>
                        {c.customer || ""}
                      </div>
                      <div style={{ fontSize: 12, color: "#667" }}>
                        {c.productName || ""}{" "}
                        <span style={{ color: "#999" }}>({c.sku || ""})</span>
                      </div>
                    </div>
                    <span style={ui.badge} title={`Rank ${c.rank}`}>
                      {c.cancelled
                        ? "Cancelled"
                        : c.claimedDept
                        ? `${c.claimedDept} • Planned`
                        : c.department || key.replace(/_/g, " ")}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3,1fr)",
                      gap: 8,
                      marginTop: 6,
                      fontSize: 12,
                    }}
                  >
                    <div>
                      <div style={ui.subtle}>Qty</div>
                      <div style={{ fontWeight: 600 }}>{c.qtyPlanned ?? 0}</div>
                    </div>
                    <div>
                      <div style={ui.subtle}>Location</div>
                      <div style={{ fontWeight: 600 }}>{c.location || "–"}</div>
                    </div>
                    <div>
                      <div style={ui.subtle}>Matrix</div>
                      <div style={{ fontWeight: 600 }}>{c.matrix || "–"}</div>
                    </div>
                    {key !== "Planned" && (
                      <div style={{ gridColumn: "1/-1" }}>
                        <div style={ui.subtle}>Scheduled Date</div>
                        <div style={{ fontWeight: 600 }}>
                          {c.plannedStart ? DATE_FMT(c.plannedStart) : "–"}
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <button style={ui.linkBtn} onClick={() => openCard(c)}>
                      Open
                    </button>
                    {key === "Scheduled" && !c.cancelled && (
                      <>
                        <button
                          style={ui.linkBtn}
                          onClick={() => {
                            // Start now quick action
                            const snap = JSON.stringify(columns);
                            setColumns((prev) => {
                              const next = JSON.parse(snap);
                              const i = next.Scheduled.findIndex(
                                (x) => x.id === c.id
                              );
                              if (i === -1) return prev;
                              const [card] = next.Scheduled.splice(i, 1);
                              card.status = "In_Production";
                              card.startedAt = todayYMD();
                              card.lifecycle = [
                                ...(card.lifecycle || []),
                                {
                                  when: new Date().toLocaleString(),
                                  text: "Started",
                                },
                              ];
                              next.In_Production.unshift(
                                assignRankTop(next.In_Production, card)
                              );
                              return next;
                            });
                            if (c.__isStep) {
                              patchStatus(c.id, {
                                fromStatus: "Scheduled",
                                toStatus: "In_Production",
                              }).catch(() => setColumns(JSON.parse(snap)));
                            }
                          }}
                        >
                          Start now
                        </button>
                        <button
                          style={ui.linkBtn}
                          onClick={() => {
                            const snap = JSON.stringify(columns);
                            setColumns((prev) => {
                              const next = JSON.parse(snap);
                              const i = next.Scheduled.findIndex(
                                (x) => x.id === c.id
                              );
                              if (i === -1) return prev;
                              next.Scheduled[i].plannedStart = todayYMD();
                              next.Scheduled[i].lifecycle = [
                                ...(next.Scheduled[i].lifecycle || []),
                                {
                                  when: new Date().toLocaleString(),
                                  text: "Rescheduled to today",
                                },
                              ];
                              return next;
                            });
                            if (c.__isStep) {
                              patchStatus(c.id, {
                                fromStatus: "Scheduled",
                                toStatus: "Scheduled",
                                plannedStart: toISOStart(todayYMD()),
                                plannedFinish: toISOEnd(todayYMD()),
                                reason: "Rescheduled to today",
                              }).catch(() => setColumns(JSON.parse(snap)));
                            }
                          }}
                        >
                          Reschedule to today
                        </button>
                      </>
                    )}
                    <div
                      style={{ marginLeft: "auto", display: "flex", gap: 6 }}
                    >
                      <button
                        title="Move up"
                        style={ui.iconBtn}
                        onClick={() => reorder("up", key, c.id)}
                      >
                        ↑
                      </button>
                      <button
                        title="Move down"
                        style={ui.iconBtn}
                        onClick={() => reorder("down", key, c.id)}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                </article>
              ))}
              {key === "Planned" && planningLoading && (
                <div style={ui.loadingRow}>Loading more…</div>
              )}
              {key === "Planned" &&
                !planningHasMore &&
                (columns.Planned || []).length > 0 && (
                  <div style={ui.endRow}>End of list</div>
                )}
            </div>
          </section>
        ))}
      </div>
      {/* Job Card popup */}
      {modalOpen && (
        <JobCardModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          card={modalCard}
          onPatchCard={onPatchCard}
          onCancelCard={onCancelCard}
          onReinstateCard={onReinstateCard}
        />
      )}
      {/* Move Gate popup */}
      {moveModal.open && (
        <MoveGateModal
          to={moveModal.to}
          fields={moveModal.fields}
          onCancel={() => setMoveModal({ ...moveModal, open: false })}
          onConfirm={(form) => confirmSchedule(form)}
        />
      )}
    </div>
  );

  // ↑/↓ fallback reordering with rank persist
  function reorder(dir, colKey, cardId) {
    setColumns((prev) => {
      const next = clone(prev);
      const arr = next[colKey] || [];
      const i = arr.findIndex((c) => c.id === cardId);
      if (i === -1) return prev;
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= arr.length) return prev;
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
      const newRank = computeRankForIndex(arr, j);
      arr[j].rank = newRank;
      if (colKey !== "Planned" && arr[j].__isStep)
        patchRank(arr[j].id, newRank);
      return next;
    });
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Styles
// ────────────────────────────────────────────────────────────────────────────
const ui = {
  wrap: { padding: 16, overflowX: "auto" },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  board: { display: "flex", gap: 16, minWidth: "1800px" },
  column: {
    flex: "0 0 340px",
    background: "#f8f9fb",
    border: "1px solid #eef0f5",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 160px)",
  },
  colHeader: {
    padding: 12,
    borderBottom: "1px solid #eef0f5",
    background: "#f1f5f9",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  cardList: { padding: 10, overflowY: "auto", flex: 1 },
  card: {
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: 10,
    marginBottom: 10,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  cardCancelled: { opacity: 0.6, filter: "grayscale(0.25)" },
  linkBtn: {
    border: "1px solid #ccc",
    borderRadius: 6,
    padding: "4px 8px",
    fontSize: 12,
    cursor: "pointer",
    background: "#fff",
  },
  iconBtn: {
    border: "1px solid #ccc",
    borderRadius: 6,
    padding: "2px 6px",
    fontSize: 12,
    cursor: "pointer",
    background: "#fff",
  },
  btnGhost: {
    border: "1px solid #ccc",
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
  btnPrimary: {
    border: "1px solid #2563eb",
    background: "#3b82f6",
    color: "#fff",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
  btnDanger: {
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
  btnWarn: {
    border: "1px solid #fbbf24",
    background: "#fef3c7",
    color: "#92400e",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
  pill: {
    border: "1px solid #dbeafe",
    background: "#eff6ff",
    color: "#1d4ed8",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
  },
  errPill: {
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
  },
  badge: {
    background: "#eef7ff",
    color: "#175cd3",
    border: "1px solid #d6e8ff",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  overduePill: {
    marginLeft: 6,
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 999,
    padding: "2px 6px",
    fontSize: 11,
  },
  statusPill: {
    marginLeft: 6,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    color: "#111827",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
  },
  cancelBadge: {
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 6,
    padding: "2px 6px",
    fontSize: 12,
  },
  subtle: { fontSize: 12, color: "#667" },
  label: { fontSize: 12, color: "#666" },
  value: { fontSize: 14, fontWeight: 500 },
  chip: {
    border: "1px solid #ddd",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    background: "#fff",
  },
  section: {
    border: "1px solid #eee",
    borderRadius: 12,
    padding: 12,
    marginTop: 10,
  },
  rowCard: {
    display: "grid",
    gridTemplateColumns:
      "minmax(36px,60px) 1.2fr 1fr 1fr 1fr minmax(90px,120px) minmax(120px,160px) auto",
    gap: 8,
    padding: 10,
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fff",
  },
  empty: { padding: 12, color: "#666", fontStyle: "italic" },
  loadingRow: { padding: 8, textAlign: "center", color: "#555" },
  endRow: { padding: 8, textAlign: "center", color: "#888" },
};

const modal = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    width: "min(860px,95vw)",
    maxHeight: "90vh",
    overflow: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
  },
};

const modalStyles = modal; // alias for JobCardModal

const tabs = {
  wrap: {
    display: "flex",
    gap: 6,
    padding: "8px 12px",
    borderBottom: "1px solid #eee",
  },
  tab: {
    border: "1px solid #ddd",
    background: "#fff",
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 12,
    cursor: "pointer",
  },
  active: { background: "#eef7ff", borderColor: "#c7e1ff", color: "#175cd3" },
};

const chatStyles = {
  box: {
    border: "1px solid #eee",
    borderRadius: 8,
    padding: 8,
    maxHeight: 280,
    overflowY: "auto",
    background: "#fff",
  },
  msg: { borderBottom: "1px dashed #eee", padding: "6px 2px" },
  meta: { fontSize: 12, color: "#666" },
};
